/* tutor-1 (C): LA PAGINA DEL TUTOR (`/tutor`). Lo que la maqueta pinta dentro de la columna, portado a la casa.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Una columna centrada de 760 como maximo con cuatro bandas: la cabecera (titulo con la lista de conversaciones y el
   "+"), "Basado en", el hilo (que se desplaza por dentro) y el cuadro de escribir pegado abajo (o la tarjeta del
   tope). La pagina no se desplaza: mide el alto del `<main>` del chasis.

   Apagado (`on: false` ya cargado) manda al Panel; en la app, solo si su raiz del carril esta activa (dormida no
   pinta nada). Lee `?doc=<id>` (preselecciona ese documento en una conversacion nueva) y `?origen=analiticas` (la
   parte 2 los usa; aqui solo se respetan). Al montar manda `tutor_opened`.

   tutor-2:
   - C.1: la mecanica del chat vive en `lib/useConversacionDelTutor.js`, la MISMA que usa la tarjeta flotante.
   - B: abajo de 768 el diseno de telefono (titulo de 17, "Basado en" de lado, la lista y el panel de documentos como
     hojas desde abajo, el cuadro sin el aviso); con el teclado abierto, la columna sigue al `visualViewport` para que
     el cuadro quede pegado al teclado (el detector es el de la barra de abajo, `useKeyboardOpen`).
   - C.6: `?conv=<id>` abre ESA conversacion al montar (si no existe, se queda en una nueva, sin aviso); si trae
     `contexto_sesion`, la tarjeta fijada va plegada encima del hilo, sin cerrar.
   - D.3: con `?origen=analiticas` la conversacion nace AL MONTAR (para que su titulo "Mis analiticas" se vea desde el
     principio), "Basado en" pinta "Tus analiticas" primero y su arranque son el saludo, el renglon y tres chips.

   tutor-3:
   - B.1 (P1): la pagina recuerda en el aparato, por cuenta, la ULTIMA conversacion abierta y el BORRADOR del cuadro
     (`lib/recuerdoDelTutor.js`; el borrador lo lleva el hook con `recordar`). Sin `?conv=`, `?doc=` ni `?origen=`,
     al montar abre la ultima (si ya no existe, 404, se olvida y queda la nueva, sin aviso) y repone su borrador.
     "+" olvida la ultima y pinta la nueva con su borrador; borrar una conversacion olvida su borrador.
   - B.2 (P2): cada casilla de "Elige documentos" aplica EN EL MOMENTO; con conversacion, el `PATCH` de siempre en
     COLA (el siguiente espera al anterior) y la casilla se marca de inmediato; si uno falla, el aviso de error de
     siempre y la casilla vuelve a como estaba. "Listo" y tocar fuera solo cierran. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRaizActiva } from '../lib/pager/activacion';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import {
  cargarTutorSiFalta, refrescarTutor, useTutorDeLaSesion,
} from '../lib/tutorDeLaSesion';
import {
  avisarEvento, borrarConversacion, cambiarConversacion, codigoDelError, leerConversaciones,
} from '../lib/tutor';
import useConversacionDelTutor from '../lib/useConversacionDelTutor';
import { guardarUltima, leerUltima, olvidarUltima } from '../lib/recuerdoDelTutor';
import {
  ACEPTA_FOTOS, Arranque, BasadoEn, CabeceraDeConversacion, Chips, Cuadro, documentosElegibles, Estado, Hilo,
  ListaDeConversaciones, MensajeAlumno, RespuestaTutor, TarjetaDelTope, VentanaArchivoGrande,
  VentanaBorrarConversacion,
} from '../components/tutor';
import TarjetaFijada from '../components/tutor/TarjetaFijada';
import { useAltoConTeclado, useEsTelefono } from '../components/tutor/pantalla';
import { useKeyboardOpen } from '../components/MobileBottomNav';

function api() { return require('../lib/api').default; }

function hastaMedianoche(ahora = new Date()) {
  const manana = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + 1, 0, 0, 5);
  return Math.max(1000, manana.getTime() - ahora.getTime());
}

export default function TutorPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const activa = useRaizActiva();
  const net = useNetworkStatus();
  const sinRed = Boolean(net && net.isOffline);
  const tutor = useTutorDeLaSesion();
  const telefono = useEsTelefono();
  const teclado = useKeyboardOpen();
  const paginaRef = useRef(null);
  const altoConTeclado = useAltoConTeclado(paginaRef, teclado);

  const [conversaciones, setConversaciones] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [pendientes, setPendientes] = useState(() => {
    const doc = params.get('doc');
    return doc ? [doc] : [];
  });
  const origenRef = useRef(params.get('origen') === 'analiticas' ? 'analiticas' : null);
  const [listaAbierta, setListaAbierta] = useState(false);
  const [elegirAbierto, setElegirAbierto] = useState(false);
  const [borrarId, setBorrarId] = useState(null);
  const fotosInputRef = useRef(null);
  const clipRef = useRef(null);
  const enfocarRef = useRef(null);

  // ------------------------------------------------------------------------------------ lo que se carga una vez
  // remate 2 (S3): la abierta viaja como `actual`, para que la lista no la esconda aunque no tenga mensajes
  const convAbiertaRef = useRef(null);
  const cargarLista = useCallback(async () => {
    try {
      setConversaciones(await leerConversaciones(undefined, convAbiertaRef.current || undefined));
    } catch (e) {
      /* la lista no es esencial: la pagina sigue */
    }
  }, []);

  const pendientesRef = useRef(pendientes);
  pendientesRef.current = pendientes;
  const chat = useConversacionDelTutor({
    user,
    sinRed,
    cuerpoAlCrear: () => ({
      documentos: pendientesRef.current, ...(origenRef.current ? { origen: origenRef.current } : {}),
    }),
    despuesDeMandar: cargarLista,
    recordar: true,
  });
  const {
    conv, setConv, mensajes, valor, setValor, fotos, contestando, estadoVivo, vivo, archivoGrande, setArchivoGrande,
    subiendo, errorDe,
  } = chat;
  convAbiertaRef.current = conv ? conv.id : null;
  const userId = user && user.id;

  // B.1: la ultima conversacion abierta en la pagina
  const convId = conv ? conv.id : null;
  useEffect(() => { if (userId && convId) guardarUltima(userId, convId); }, [userId, convId]);

  // --------------------------------------------------------------------------------------- la sesion del tutor
  // dormida en el carril de la app no pide nada (la regla de PAGER-1); el chasis ya hizo la carga de la sesion
  useEffect(() => { if (user && activa) cargarTutorSiFalta(user); }, [user, activa]);

  useEffect(() => {
    if (!activa || !tutor.listo || tutor.on) return;
    navigate('/dashboard', { replace: true });
  }, [activa, tutor.listo, tutor.on, navigate]);

  const abiertoRef = useRef(false);
  useEffect(() => {
    if (!activa || !tutor.on || abiertoRef.current) return;
    abiertoRef.current = true;
    avisarEvento('tutor_opened');
  }, [activa, tutor.on]);

  // al volver a la pestana y a medianoche local: el tope puede haberse reiniciado
  useEffect(() => {
    if (!user || !tutor.on) return undefined;
    const alVolver = () => { if (document.visibilityState === 'visible') refrescarTutor(user); };
    document.addEventListener('visibilitychange', alVolver);
    const reloj = setTimeout(() => refrescarTutor(user), hastaMedianoche());
    return () => { document.removeEventListener('visibilitychange', alVolver); clearTimeout(reloj); };
  }, [user, tutor.on]);

  useEffect(() => {
    if (!activa || !tutor.on) return;
    cargarLista();
    (async () => {
      try {
        const res = await api().get('/documents');
        const lista = (res && res.data && res.data.documents) || [];
        setDocumentos(documentosElegibles(lista));
      } catch (e) {
        /* sin lista, el panel de elegir sale vacio */
      }
    })();
  }, [activa, tutor.on, cargarLista]);

  // C.6 y D.3: lo que la URL pide al montar (una vez por montaje).
  const deLaUrlRef = useRef(false);
  const { cargar, asegurarConversacion, reiniciar } = chat;
  useEffect(() => {
    if (!activa || !tutor.on || deLaUrlRef.current) return;
    deLaUrlRef.current = true;
    const id = params.get('conv');
    if (id) {
      cargar(id).catch(() => { /* si no existe, la conversacion nueva, sin aviso */ });
    } else if (origenRef.current === 'analiticas') {
      asegurarConversacion().catch((e) => errorDe(codigoDelError(e)));
    } else if (!params.get('doc')) {
      // B.1: sin parametros, la ultima conversacion (si ya no existe, se olvida y queda la nueva, sin aviso)
      const ultima = leerUltima(userId);
      if (!ultima) { reiniciar(); return; }
      cargar(ultima).catch((e) => {
        if (e && e.response && e.response.status === 404) olvidarUltima(userId);
        reiniciar();
      });
    }
  }, [activa, tutor.on, params, cargar, asegurarConversacion, reiniciar, errorDe, userId]);

  // ------------------------------------------------------------------------------------------ las acciones
  const abrir = useCallback(async (id) => {
    setListaAbierta(false);
    if (contestando) return;
    try {
      await cargar(id);
    } catch (e) {
      errorDe(codigoDelError(e));
    }
  }, [contestando, cargar, errorDe]);

  const nueva = useCallback(() => {
    setListaAbierta(false);
    if (contestando) return;
    olvidarUltima(userId);
    reiniciar();
    setPendientes([]);
    origenRef.current = null;
  }, [contestando, reiniciar, userId]);

  // B.2: cada cambio se pinta de inmediato y su `PATCH` va en cola; la respuesta solo se aplica si es la del ultimo
  // cambio (para que una vieja no deshaga lo que el alumno ya marco); si falla, vuelve a lo de antes de ese cambio.
  const colaRef = useRef(Promise.resolve());
  const turnoRef = useRef(0);
  const cambiarDocumentos = useCallback((ids) => {
    if (!conv) { setPendientes(ids); return colaRef.current; }
    const id = conv.id;
    const antes = conv.documentos || [];
    turnoRef.current += 1;
    const turno = turnoRef.current;
    setConv((x) => (x && x.id === id ? { ...x, documentos: ids } : x));
    colaRef.current = colaRef.current.then(async () => {
      try {
        const c = await cambiarConversacion(id, { documentos: ids });
        if (turno === turnoRef.current) {
          setConv((x) => (x && x.id === id ? { ...x, documentos: c.documentos, recortados: c.recortados || [] } : x));
        }
      } catch (e) {
        if (turno === turnoRef.current) setConv((x) => (x && x.id === id ? { ...x, documentos: antes } : x));
        errorDe(codigoDelError(e));
      }
    });
    return colaRef.current;
  }, [conv, errorDe, setConv]);

  const renombrar = useCallback(async (id, titulo) => {
    try {
      const c = await cambiarConversacion(id, { titulo });
      setConversaciones((l) => l.map((x) => (x.id === id ? { ...x, titulo: c.titulo } : x)));
      setConv((x) => (x && x.id === id ? { ...x, titulo: c.titulo } : x));
    } catch (e) {
      errorDe(codigoDelError(e));
    }
  }, [errorDe, setConv]);

  const confirmarBorrado = useCallback(async () => {
    const id = borrarId;
    setBorrarId(null);
    try {
      await borrarConversacion(id);
      chat.olvidarBorrador(id);
      if (leerUltima(userId) === id) olvidarUltima(userId);
      setConversaciones((l) => l.filter((x) => x.id !== id));
      if (conv && conv.id === id) nueva();
    } catch (e) {
      errorDe(codigoDelError(e));
    }
  }, [borrarId, conv, nueva, errorDe, chat, userId]);

  // tutor-1 (remate, firma de Carlos del 21-oct): "Practicar este tema" abre EXAM con el tema elegido. Adaptive nunca
  // filtra por tema (#271 enmendada), asi que ahi el tema se perdia.
  const practicar = useCallback((mensaje) => {
    avisarEvento('tutor_practice_click');
    const doc = mensaje.tema_documento || (conv && conv.documentos && conv.documentos[0]) || '';
    navigate(`/study?mode=exam&doc=${encodeURIComponent(doc)}&topic=${encodeURIComponent(mensaje.tema)}`);
  }, [conv, navigate]);

  // ----------------------------------------------------------------------------------------------- el pintado
  const ultimoDelTutor = useMemo(() => {
    for (let i = mensajes.length - 1; i >= 0; i -= 1) if (mensajes[i].rol === 'tutor') return mensajes[i];
    return null;
  }, [mensajes]);

  if (!tutor.on) return null;

  const alTope = tutor.tope != null && tutor.usados >= tutor.tope;
  const conHilo = mensajes.length > 0 || contestando;
  const elegidos = conv ? (conv.documentos || []) : pendientes;
  const deAnaliticas = conv ? conv.origen === 'analiticas' : origenRef.current === 'analiticas';
  const mandar = chat.mandar;

  return (
    <div ref={paginaRef} className="h-full min-h-0 flex flex-col" data-testid="tutor-page"
      data-teclado={altoConTeclado ? 'abierto' : 'cerrado'} style={altoConTeclado ? { height: altoConTeclado } : undefined}>
      <div className="w-full max-w-[760px] mx-auto flex flex-col flex-1 min-h-0 relative" data-testid="tutor-columna">
        <CabeceraDeConversacion titulo={conv && conv.titulo} listaAbierta={listaAbierta}
          alAbrirLista={() => setListaAbierta((v) => !v)} alNueva={nueva}>
          {listaAbierta ? (
            <ListaDeConversaciones enHoja={telefono} conversaciones={conversaciones} actual={conv && conv.id} alAbrir={abrir}
              alNueva={nueva} alRenombrar={renombrar}
              alBorrar={(id) => { if (telefono) setListaAbierta(false); setBorrarId(id); }}
              alCerrar={() => setListaAbierta(false)} />
          ) : null}
        </CabeceraDeConversacion>
        <BasadoEn documentos={documentos} elegidos={elegidos} archivos={(conv && conv.archivos) || []}
          recortados={(conv && conv.recortados) || []} abierto={elegirAbierto} alAbrir={() => setElegirAbierto((v) => !v)}
          alCerrar={() => setElegirAbierto(false)} alCambiarDocumentos={cambiarDocumentos}
          alQuitarArchivo={chat.quitarArchivoDeLaConversacion} subiendo={subiendo} conAnaliticas={deAnaliticas}
          conRespuestas={mensajes.some((m) => m.rol === 'tutor')} />
        {conv && conv.contexto_sesion ? (
          <div className="pb-3"><TarjetaFijada key={conv.id} contexto={conv.contexto_sesion} /></div>
        ) : null}
        {conHilo ? (
          <Hilo marca={`${mensajes.length}:${vivo ? vivo.texto.length : 0}:${estadoVivo ? estadoVivo.tipo : ''}`}
            contestando={contestando} clave={conv ? conv.id : null}>
            {mensajes.map((m) => (m.rol === 'tutor' ? (
              <RespuestaTutor key={m.id} mensaje={m} alOpinar={(v) => chat.opinarDe(m, v)}
                alAbrirFuentes={() => avisarEvento('tutor_sources_opened')} />
            ) : (
              <MensajeAlumno key={m.id} mensaje={m} />
            )))}
            {estadoVivo ? <Estado estado={estadoVivo} /> : null}
            {vivo && vivo.texto ? <RespuestaTutor mensaje={{ id: 'vivo', texto: vivo.texto }} escribiendo /> : null}
            {!contestando && ultimoDelTutor && ultimoDelTutor === mensajes[mensajes.length - 1] ? (
              <div>
                <Chips chips={ultimoDelTutor.chips} tema={ultimoDelTutor.tema} apagados={alTope}
                  alTocar={(c) => mandar(c)} alPracticar={() => practicar(ultimoDelTutor)} />
              </div>
            ) : null}
          </Hilo>
        ) : (
          <Arranque user={user} temaFlojo={tutor.temaFlojo} analiticas={deAnaliticas}
            alPreguntar={(texto, mandarYa, docId) => {
              if (docId && !elegidos.includes(docId)) {
                if (conv) cambiarDocumentos([...elegidos, docId]); else setPendientes([...elegidos, docId]);
              }
              if (mandarYa) mandar(texto); else { setValor(texto); if (enfocarRef.current) enfocarRef.current(); }
            }}
            alElegirDocumentos={() => setElegirAbierto(true)}
            alElegirFotos={() => fotosInputRef.current && fotosInputRef.current.click()} />
        )}
        <input ref={fotosInputRef} type="file" accept={ACEPTA_FOTOS} multiple className="hidden" data-testid="tutor-input-fotos-arranque"
          onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) chat.elegirFotos(fs); }} />
        {alTope ? (
          <TarjetaDelTope plan={tutor.plan} alIr={(ruta) => { if (ruta === '/upgrade') avisarEvento('tutor_upsell_click', { plan: tutor.plan || '' }); navigate(ruta); }} />
        ) : (
          <Cuadro valor={valor} alCambiar={setValor} fotos={fotos} alQuitarFoto={chat.quitarFoto} alElegirFotos={chat.elegirFotos}
            alElegirArchivo={chat.elegirArchivo} alMandar={() => mandar()} alDetener={chat.detener} contestando={contestando}
            usados={tutor.usados} tope={tutor.tope} apagado={sinRed} enfocarRef={enfocarRef} abrirClipRef={clipRef}
            conAviso={!telefono} />
        )}
      </div>
      {borrarId ? <VentanaBorrarConversacion alConfirmar={confirmarBorrado} alCerrar={() => setBorrarId(null)} /> : null}
      {archivoGrande ? (
        <VentanaArchivoGrande alCerrar={() => setArchivoGrande(null)}
          alSubirAlPanel={() => { const f = archivoGrande; setArchivoGrande(null); navigate('/dashboard', { state: { archivoDelTutor: f } }); }}
          alElegirOtro={() => { setArchivoGrande(null); if (clipRef.current) clipRef.current(); }} />
      ) : null}
    </div>
  );
}
